/**
 * Reporte de inventario de motores en PDF (jsPDF + autotable) y Excel (xlsx).
 *
 * Antes el PDF se armaba con react-pdf dentro de un PDFViewer: cada fila era
 * un árbol de componentes con layout flex, y con miles de chasis la pantalla
 * se congelaba. autotable dibuja la tabla directo en el PDF y tarda poco
 * aunque sean miles de filas.
 */
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";
import { nombreEmpresa } from "lib/multiempresa";

const NAVY = [24, 88, 107];
const AZUL = [59, 130, 246];
const CEBRA = [246, 248, 251];
const GRIS = [100, 116, 139];

const TIPOS = { 0: "Todos", 1: "Incautados", 2: "Nuevo", 3: "Usado" };

const num = (v) => {
  const n = parseFloat(String(v ?? "").replace(/,/g, "").trim());
  return Number.isFinite(n) ? n : 0;
};
const numONulo = (v) => {
  if (v == null || String(v).trim() === "") return null;
  const n = parseFloat(String(v).replace(/,/g, "").trim());
  return Number.isFinite(n) ? n : null;
};
const fmt = (v) =>
  num(v).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const txt = (v) => String(v ?? "").trim();

const nombreSucursal = (r, sucursalMap) => {
  const code = txt(r.sucursal);
  return (sucursalMap && sucursalMap[code]) || code;
};

/**
 * Columnas del reporte. `pdf` = sale en el PDF (hoja horizontal, ancho
 * limitado); el Excel lleva todas. `monto` = numérica, alineada a la derecha.
 */
function columnasReporte({ sinCostos, incautados, multiempresa, sucursalMap }) {
  const cols = [
    { titulo: "Sucursal", pdf: true, get: (r) => nombreSucursal(r, sucursalMap), ancho: 20 },
    ...(multiempresa && multiempresa.activo
      ? [
          {
            titulo: "Empresa",
            pdf: true,
            get: (r) => nombreEmpresa(multiempresa.empresas, r.empresa || 1),
            ancho: 18,
          },
        ]
      : []),
    { titulo: "Marca", pdf: true, get: (r) => txt(r.marca), ancho: 16 },
    { titulo: "Modelo", pdf: true, get: (r) => txt(r.modelo), ancho: 20 },
    { titulo: "Color", pdf: true, get: (r) => txt(r.colore), ancho: 12 },
    {
      titulo: "Año",
      pdf: false,
      get: (r) => (r.ano != null && String(r.ano) !== "0" ? Number(r.ano) || txt(r.ano) : ""),
      ancho: 7,
    },
    { titulo: "Chasis", pdf: true, get: (r) => txt(r.chasis), ancho: 24 },
    ...(!sinCostos
      ? [{ titulo: "Costo", pdf: true, monto: true, get: (r) => num(r.costo ?? r.cost), ancho: 14 }]
      : []),
    { titulo: "Precio", pdf: true, monto: true, get: (r) => num(r.precio ?? r.price), ancho: 14 },
    {
      titulo: "Min. precio",
      pdf: false,
      monto: true,
      get: (r) => num(r.mprecio ?? r.mprice),
      ancho: 14,
    },
    { titulo: "Status", pdf: true, get: (r) => txt(r.estado), ancho: 9 },
    { titulo: "Condición", pdf: true, get: (r) => txt(r.condicion), ancho: 12 },
    ...(incautados
      ? [
          { titulo: "Cliente", pdf: true, get: (r) => txt(r.nombres), ancho: 28 },
          {
            titulo: "Balance actual",
            pdf: true,
            monto: true,
            get: (r) => numONulo(r.balanceactual),
            ancho: 15,
          },
          {
            titulo: "Proyectado",
            pdf: true,
            monto: true,
            get: (r) => numONulo(r.proyectado),
            ancho: 15,
          },
        ]
      : []),
  ];
  return cols;
}

function resumen(filas, sinCostos) {
  const cond = (r) => txt(r.condicion).toUpperCase();
  const porMarca = {};
  for (const r of filas) {
    const m = txt(r.marca).toUpperCase() || "SIN MARCA";
    porMarca[m] = (porMarca[m] || 0) + 1;
  }
  return {
    unidades: filas.length,
    costo: sinCostos ? null : filas.reduce((s, r) => s + num(r.costo ?? r.cost), 0),
    valor: filas.reduce((s, r) => s + num(r.precio ?? r.price), 0),
    nuevos: filas.filter((r) => cond(r).startsWith("N")).length,
    usados: filas.filter((r) => cond(r).startsWith("U")).length,
    marcas: Object.entries(porMarca).sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0])),
  };
}

function textoFiltros({ status, nosucursal, marca, modelo, empresaFiltro, multiempresa }) {
  const sucursal = !nosucursal || nosucursal.label === "0-TODO" ? "Todas" : nosucursal.label;
  const partes = [
    ["Tipo", TIPOS[status] || "Todos"],
    ["Sucursal", sucursal],
    ["Marca", txt(marca) || "Todas"],
    ["Modelo", txt(modelo) || "Todos"],
  ];
  if (multiempresa && multiempresa.activo) {
    partes.push([
      "Empresa",
      String(empresaFiltro) === "0" ? "Todas" : nombreEmpresa(multiempresa.empresas, empresaFiltro),
    ]);
  }
  return partes;
}

const fechaGenerado = () =>
  new Date().toLocaleString("es-DO", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

const sello = () => {
  const d = new Date();
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}${p(d.getMonth() + 1)}${p(d.getDate())}_${p(d.getHours())}${p(d.getMinutes())}`;
};

/**
 * Genera el PDF y lo abre en una pestaña nueva (o lo descarga si el
 * navegador bloquea la pestaña). Debe llamarse dentro del clic: todo es
 * síncrono, así que el window.open no se bloquea.
 */
export function generarPdfInventario(filas, opciones) {
  const { nombreNegocio, logoEmpresa, sinCostos } = opciones;
  const cols = columnasReporte(opciones).filter((c) => c.pdf);
  const res = resumen(filas, sinCostos);
  const filtros = textoFiltros(opciones);
  const generado = fechaGenerado();

  const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
  const ancho = doc.internal.pageSize.getWidth();
  const alto = doc.internal.pageSize.getHeight();
  const M = 28;

  // Logo: se prepara una sola vez y se reutiliza en cada página.
  let logo = null;
  if (logoEmpresa) {
    try {
      const props = doc.getImageProperties(logoEmpresa);
      const h = 34;
      const w = Math.min(110, (props.width / props.height) * h);
      logo = { src: logoEmpresa, w, h };
    } catch (e) {
      logo = null;
    }
  }

  const encabezado = () => {
    let x = M;
    if (logo) {
      try {
        doc.addImage(logo.src, x, M - 4, logo.w, logo.h, "logo", "FAST");
        x += logo.w + 10;
      } catch (e) {
        /* sin logo */
      }
    }
    doc.setFont("helvetica", "bold");
    doc.setFontSize(15);
    doc.setTextColor(...NAVY);
    doc.text(String(nombreNegocio || "Empresa").toUpperCase(), x, M + 12);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8.5);
    doc.setTextColor(...GRIS);
    doc.text("REPORTE DE INVENTARIO DE MOTORES", x, M + 25);

    doc.setFontSize(7.5);
    const lineas = [
      filtros.slice(0, 2).map(([k, v]) => `${k}: ${v}`).join("   ·   "),
      filtros.slice(2).map(([k, v]) => `${k}: ${v}`).join("   ·   "),
      `Motores: ${res.unidades}`,
    ];
    lineas.forEach((l, i) => doc.text(l, ancho - M, M + 4 + i * 10, { align: "right" }));

    doc.setFillColor(...NAVY);
    doc.rect(M, M + 34, ancho - 2 * M, 2.5, "F");
    doc.setFillColor(...AZUL);
    doc.rect(M, M + 38, ancho - 2 * M, 0.8, "F");
  };

  const totales = cols.map((c, i) => {
    if (c.titulo === "Chasis") return "TOTALES";
    if (c.titulo === "Costo") return fmt(res.costo);
    if (c.titulo === "Precio") return fmt(res.valor);
    if (i === 0) return `${res.unidades} unidades`;
    return "";
  });

  const montoIdx = {};
  cols.forEach((c, i) => {
    if (c.monto) montoIdx[i] = { halign: "right" };
  });

  autoTable(doc, {
    head: [cols.map((c) => c.titulo)],
    body: filas.map((r) =>
      cols.map((c) => {
        const v = c.get(r);
        return c.monto ? (v == null ? "—" : fmt(v)) : v;
      })
    ),
    foot: [totales],
    showFoot: "lastPage",
    margin: { top: M + 48, left: M, right: M, bottom: 30 },
    theme: "plain",
    styles: {
      font: "helvetica",
      fontSize: 7,
      cellPadding: { top: 3, bottom: 3, left: 4, right: 4 },
      textColor: [26, 36, 48],
      lineColor: [226, 232, 240],
      lineWidth: { bottom: 0.5 },
      overflow: "ellipsize",
    },
    headStyles: { fillColor: NAVY, textColor: 255, fontStyle: "bold", fontSize: 7.2 },
    footStyles: {
      fillColor: [238, 242, 247],
      textColor: NAVY,
      fontStyle: "bold",
      lineColor: NAVY,
      lineWidth: { top: 1.2 },
    },
    alternateRowStyles: { fillColor: CEBRA },
    columnStyles: montoIdx,
    didParseCell: (data) => {
      if (data.section !== "body" && montoIdx[data.column.index]) data.cell.styles.halign = "right";
    },
    didDrawPage: () => {
      encabezado();
      doc.setFont("helvetica", "normal");
      doc.setFontSize(6.5);
      doc.setTextColor(...GRIS);
      doc.text(`Generado el ${generado}`, M, alto - 14);
    },
  });

  // Resumen en tres tarjetas al final (nueva página si no caben).
  const marcasVis = res.marcas.slice(0, 12);
  const otras = res.marcas.slice(12).reduce((s, [, c]) => s + c, 0);
  const lineasMarca = [...marcasVis, ...(otras > 0 ? [["OTRAS", otras]] : [])];
  const altoTarjeta = 28 + Math.max(3, lineasMarca.length) * 11;
  let y = doc.lastAutoTable.finalY + 16;
  if (y + altoTarjeta > alto - 34) {
    doc.addPage();
    encabezado();
    y = M + 52;
  }
  const gap = 10;
  const anchoT = (ancho - 2 * M - 2 * gap) / 3;
  const tarjeta = (i, titulo, datos) => {
    const x = M + i * (anchoT + gap);
    doc.setDrawColor(226, 232, 240);
    doc.setFillColor(...CEBRA);
    doc.roundedRect(x, y, anchoT, altoTarjeta, 5, 5, "FD");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(7.5);
    doc.setTextColor(...NAVY);
    doc.text(titulo.toUpperCase(), x + 10, y + 15);
    doc.setDrawColor(203, 213, 225);
    doc.line(x + 10, y + 19, x + anchoT - 10, y + 19);
    datos.forEach(([k, v], j) => {
      const yy = y + 31 + j * 11;
      doc.setFont("helvetica", "normal");
      doc.setTextColor(...GRIS);
      doc.text(String(k), x + 10, yy);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(26, 36, 48);
      doc.text(String(v), x + anchoT - 10, yy, { align: "right" });
    });
  };
  tarjeta(0, "Totales", [
    ["Unidades", String(res.unidades)],
    ...(!sinCostos ? [["Costo total", fmt(res.costo)]] : []),
    ["Valor total", fmt(res.valor)],
  ]);
  tarjeta(1, "Datos de motores", [
    ["Motores nuevos", String(res.nuevos)],
    ["Motores usados", String(res.usados)],
  ]);
  tarjeta(2, "Por marca", lineasMarca.map(([m, c]) => [m, String(c)]));

  // Numeración "Página X de Y" una vez que se conoce el total.
  const total = doc.getNumberOfPages();
  for (let p = 1; p <= total; p++) {
    doc.setPage(p);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(6.5);
    doc.setTextColor(...GRIS);
    doc.text(`Página ${p} de ${total}`, ancho - M, alto - 14, { align: "right" });
  }

  doc.setProperties({ title: "Reporte de inventario de motores" });
  const nombre = `InventarioMotores_${sello()}.pdf`;
  const url = doc.output("bloburl");
  const win = window.open(url, "_blank");
  if (!win) doc.save(nombre);
  return doc;
}

/**
 * Excel con dos hojas: "Inventario" (montos como números, con fila de
 * totales) y "Resumen" (totales, nuevos/usados y conteo por marca).
 */
export function exportarExcelInventario(filas, opciones) {
  const { nombreNegocio, sinCostos } = opciones;
  const cols = columnasReporte(opciones);
  const res = resumen(filas, sinCostos);
  const filtros = textoFiltros(opciones);

  const encabezado = [
    [String(nombreNegocio || "").trim() || "Empresa"],
    ["Reporte de inventario de motores"],
    [filtros.map(([k, v]) => `${k}: ${v}`).join("  ·  ")],
    [`Generado el ${fechaGenerado()}`],
    [],
  ];
  const filaTitulos = encabezado.length;
  const cuerpo = filas.map((r) => cols.map((c) => c.get(r)));
  const totales = cols.map((c, i) => {
    if (i === 0) return `TOTALES (${res.unidades})`;
    if (c.titulo === "Costo") return res.costo;
    if (c.titulo === "Precio") return res.valor;
    return "";
  });

  const hoja = XLSX.utils.aoa_to_sheet([
    ...encabezado,
    cols.map((c) => c.titulo),
    ...cuerpo,
    totales,
  ]);
  hoja["!cols"] = cols.map((c) => ({ wch: c.ancho }));
  // Formato de miles en las columnas de montos.
  const ultimaFila = filaTitulos + cuerpo.length + 1;
  cols.forEach((c, ci) => {
    if (!c.monto) return;
    for (let ri = filaTitulos + 1; ri <= ultimaFila; ri++) {
      const ref = XLSX.utils.encode_cell({ r: ri, c: ci });
      if (hoja[ref] && hoja[ref].t === "n") hoja[ref].z = "#,##0.00";
    }
  });
  // Filtro automático sobre la tabla (sin la fila de totales).
  hoja["!autofilter"] = {
    ref: XLSX.utils.encode_range({
      s: { r: filaTitulos, c: 0 },
      e: { r: filaTitulos + cuerpo.length, c: cols.length - 1 },
    }),
  };

  const hojaRes = XLSX.utils.aoa_to_sheet([
    ["Resumen"],
    [],
    ["Unidades", res.unidades],
    ...(!sinCostos ? [["Costo total", res.costo]] : []),
    ["Valor total", res.valor],
    ["Motores nuevos", res.nuevos],
    ["Motores usados", res.usados],
    [],
    ["Marca", "Unidades"],
    ...res.marcas,
  ]);
  hojaRes["!cols"] = [{ wch: 24 }, { wch: 16 }];
  // Montos del resumen: costo (si se muestra) y valor, justo debajo de Unidades.
  (sinCostos ? ["B4"] : ["B4", "B5"]).forEach((ref) => {
    if (hojaRes[ref]) hojaRes[ref].z = "#,##0.00";
  });

  const libro = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(libro, hoja, "Inventario");
  XLSX.utils.book_append_sheet(libro, hojaRes, "Resumen");
  XLSX.writeFile(libro, `InventarioMotores_${sello()}.xlsx`);
}
